// ── Tlhakiso Stokvel — Firestore data layer ──
//
// A NON-CUSTODIAL transparency + reconciliation layer for savings groups. Lives in
// a top-level `stokvels` collection (independent of accounting `projects`), but in
// the SAME Firebase project (tlhakiso-ff53f) so the web module and the native app
// share one backend. Membership/roles mirror the projects pattern:
//   stokvels/{id}: { name, type, frequency, contributionAmount, code, bankLabel,
//                    members: { [uid]: { role, email, name, reference } },
//                    memberUids: [uid…] }
// Roles: 'chair' (admin) | 'treasurer' (records money) | 'member' (read-only).
// Subcollections: contributions, payouts, loans, repayments, auditLog, meta,
// bankFeedTransactions (the last two REUSE the existing Stitch bank-feed plumbing).
import {
  doc, setDoc, updateDoc, getDoc, getDocs, addDoc, collection, query, where, orderBy, limit, writeBatch, deleteDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { codeFromName, nextMemberReference, bankLabelFrom, makeJoinCode } from "./stokvelHelpers";

function actor() {
  const u = auth.currentUser;
  return (u && (u.email || u.uid)) || "unauthenticated";
}

function makeId(name) {
  const slug = String(name || "stokvel").toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 28) || "stokvel";
  const rand = Math.random().toString(36).slice(2, 7);
  return slug + "-" + rand;
}

export async function logStokvelAudit(id, entry) {
  try {
    await addDoc(collection(db, "stokvels", id, "auditLog"), {
      action: entry.action || "unknown",
      summary: entry.summary || "",
      details: entry.details || null,
      actor: actor(),
      at: serverTimestamp(),
    });
  } catch (e) { /* non-fatal */ }
}

// ── Group CRUD + membership ──

export async function listStokvelsForUser(uid) {
  const q = query(collection(db, "stokvels"), where("memberUids", "array-contains", uid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function loadStokvel(id) {
  const snap = await getDoc(doc(db, "stokvels", id));
  return snap.exists() ? { id, ...snap.data() } : null;
}

export async function createStokvel(data, user) {
  const id = makeId(data.name);
  const code = (data.code || codeFromName(data.name)).toUpperCase();
  const members = {
    [user.uid]: { role: "chair", email: (user.email || "").toLowerCase(), name: data.creatorName || user.email || "Chairperson", reference: code + "01" },
  };
  await setDoc(doc(db, "stokvels", id), {
    name: data.name,
    type: data.type || "savings",
    frequency: data.frequency || "monthly",
    contributionAmount: Number(data.contributionAmount) || 0,
    code,
    // Structured banking details of the group's OWN account (reference only).
    bankName: data.bankName || "",
    accountName: data.accountName || "",
    accountNumber: data.accountNumber || "",
    branchCode: data.branchCode || "",
    bankLabel: data.bankLabel || bankLabelFrom(data),
    // Shareable member-invite code, generated up front and ON so the chair can
    // invite members the moment the group exists (no need to hunt for a "turn on
    // invites" toggle). Distinct from `code` above (the internal short-code used
    // for member reference numbers). Chair can still rotate/disable it later via
    // setStokvelJoinCode. Respect an explicitly-passed code if the caller set one.
    joinCode: data.joinCode || makeJoinCode(),
    joinCodeActive: true,
    members,
    memberUids: [user.uid],
    // 14-day free trial from creation: recording works like a paid plan until this
    // date, after which the chair must subscribe. See subscriptionState().
    trialStartedAt: serverTimestamp(),
    trialEndsAt: Timestamp.fromMillis(Date.now() + 14 * 24 * 60 * 60 * 1000),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "create_stokvel", summary: "Created stokvel " + data.name });
  return id;
}

export async function saveStokvel(id, patch) {
  await setDoc(doc(db, "stokvels", id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "update_stokvel", summary: "Updated group settings", details: patch });
}

export async function addStokvelMember(id, { uid, email, name, role, reference, phone, openingBalance }) {
  const ref = doc(db, "stokvels", id);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const members = { ...(data.members || {}) };
  // Keep the reference the member has ALWAYS used where supplied (a migrated stokvel
  // shouldn't force everyone onto a new code); only auto-generate when none given.
  const memberRef = reference || nextMemberReference(data, members);
  // App member (has a uid → can sign in and see) vs tracked-only member (no uid).
  const key = uid || ("tracked:" + memberRef);
  // phone (optional) enables the read-only WhatsApp self-service bot for this member.
  // openingBalance (optional) = lifetime contributions brought forward at migration.
  members[key] = { role: role || "member", email: (email || "").toLowerCase(), name: name || email || memberRef, reference: memberRef, phone: phone || "", tracked: !uid, openingBalance: Number(openingBalance) || 0 };
  const memberUids = Object.keys(members).filter((k) => !k.startsWith("tracked:"));
  await setDoc(ref, { members, memberUids, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "add_member", summary: "Added " + (name || email || memberRef) + " (" + (role || "member") + ", ref " + memberRef + ")", details: { key, reference: memberRef } });
  return { key, reference: memberRef };
}

// Enable/disable a self-onboarding invite code for a stokvel. The chair sets this
// (rules allow only the chair to update the group doc); members then join with the
// code either via the app link (/join/CODE) or by texting it to the WhatsApp bot.
// The actual join is done server-side (Cloud Function, admin SDK) because a new
// member cannot add themselves to the members map under the security rules.
export async function setStokvelJoinCode(id, code, active) {
  const ref = doc(db, "stokvels", id);
  await setDoc(ref, { joinCode: code || "", joinCodeActive: !!active, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, {
    action: active ? "enable_invite" : "disable_invite",
    summary: active ? ("Invite code enabled (" + code + ")") : "Invite code disabled",
    details: { code: code || "", active: !!active },
  });
}

export async function updateStokvelMember(id, key, patch) {
  const ref = doc(db, "stokvels", id);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const members = { ...(data.members || {}) };
  if (!members[key]) return;
  members[key] = { ...members[key], ...patch };
  await setDoc(ref, { members, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "update_member", summary: "Updated member " + (members[key].name || key), details: { key, patch } });
}

export async function removeStokvelMember(id, key) {
  const ref = doc(db, "stokvels", id);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const members = { ...(data.members || {}) };
  const removed = members[key];
  delete members[key];
  const memberUids = Object.keys(members).filter((k) => !k.startsWith("tracked:"));
  // IMPORTANT: use updateDoc (not setDoc merge:true) — a merge write NEVER deletes a
  // map key, so the removed member would otherwise reappear on reload. updateDoc
  // replaces the whole members field with the map that no longer has this key.
  await updateDoc(ref, { members, memberUids, updatedAt: serverTimestamp() });
  await logStokvelAudit(id, { action: "remove_member", summary: "Removed " + ((removed && removed.name) || key), details: { key } });
}

// ── Contributions ──

export async function loadContributions(id) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "contributions"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addContribution(id, c) {
  const ref = await addDoc(collection(db, "stokvels", id, "contributions"), {
    memberUid: c.memberUid || null,
    memberName: c.memberName || "",
    reference: c.reference || "",
    date: c.date,
    amount: Number(c.amount) || 0,
    method: c.method || "manual",
    status: c.status || "confirmed",
    source: c.source || "manual",
    __bankTxnId: c.__bankTxnId || null,
    note: c.note || "",
    recordedBy: actor(),
    at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_contribution", summary: "Recorded R" + (Number(c.amount) || 0) + " from " + (c.memberName || c.reference || "member"), details: { contributionId: ref.id } });
  return ref.id;
}

// Batch-record many contributions (e.g. from a bank-feed reconciliation).
export async function addContributions(id, list) {
  if (!list || !list.length) return [];
  const batch = writeBatch(db);
  const ids = [];
  list.forEach((c) => {
    const ref = doc(collection(db, "stokvels", id, "contributions"));
    batch.set(ref, {
      memberUid: c.memberUid || null, memberName: c.memberName || "", reference: c.reference || "",
      date: c.date, amount: Number(c.amount) || 0, method: c.method || "bank",
      status: "confirmed", source: c.source || "bankFeed", __bankTxnId: c.__bankTxnId || null,
      note: c.note || "", recordedBy: actor(), at: serverTimestamp(),
    });
    ids.push(ref.id);
  });
  batch.set(doc(collection(db, "stokvels", id, "auditLog")), { action: "reconcile_contributions", summary: "Reconciled " + list.length + " contribution(s) from the bank feed", details: { count: list.length }, actor: actor(), at: serverTimestamp() });
  await batch.commit();
  return ids;
}

export async function voidContribution(id, cid) {
  await setDoc(doc(db, "stokvels", id, "contributions", cid), { status: "void", updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "void_contribution", summary: "Voided contribution " + cid, details: { cid } });
}

// ── Payouts ──

export async function loadPayouts(id) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "payouts"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addPayout(id, p) {
  const ref = await addDoc(collection(db, "stokvels", id, "payouts"), {
    memberUid: p.memberUid || null, memberName: p.memberName || "", date: p.date,
    amount: Number(p.amount) || 0, reason: p.reason || "", source: p.source || "manual",
    receipts: p.receipts || (p.receipt ? [p.receipt] : []),
    recordedBy: actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_payout", summary: "Recorded payout of R" + (Number(p.amount) || 0) + " to " + (p.memberName || "group"), details: { payoutId: ref.id } });
  return ref.id;
}

export async function deletePayout(id, pid) {
  await deleteDoc(doc(db, "stokvels", id, "payouts", pid));
  await logStokvelAudit(id, { action: "delete_payout", summary: "Deleted payout " + pid, details: { pid } });
}

// ── Loans (to members) + repayments ──

export async function loadLoans(id) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "loans"), orderBy("issueDate", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addLoan(id, l) {
  const ref = await addDoc(collection(db, "stokvels", id, "loans"), {
    memberUid: l.memberUid || null, memberName: l.memberName || "", reference: l.reference || "",
    principal: Number(l.principal) || 0,
    interestType: l.interestType || "none", interestRate: Number(l.interestRate) || 0,
    // Agreed repayment plan (target count only; outstanding stays live). 0 = open-ended.
    numberOfRepayments: Number(l.numberOfRepayments) || 0,
    issueDate: l.issueDate, dueDate: l.dueDate || null,
    status: "active", note: l.note || "", recordedBy: actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_loan", summary: "Recorded loan of R" + (Number(l.principal) || 0) + " to " + (l.memberName || "member"), details: { loanId: ref.id } });
  return ref.id;
}

export async function updateLoan(id, lid, patch) {
  await setDoc(doc(db, "stokvels", id, "loans", lid), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "update_loan", summary: "Updated loan " + lid, details: { lid, patch } });
}

export async function loadRepayments(id) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "repayments"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addRepayment(id, r) {
  const ref = await addDoc(collection(db, "stokvels", id, "repayments"), {
    loanId: r.loanId, memberUid: r.memberUid || null, memberName: r.memberName || "",
    date: r.date, amount: Number(r.amount) || 0, source: r.source || "manual",
    __bankTxnId: r.__bankTxnId || null, note: r.note || "", recordedBy: actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_repayment", summary: "Recorded repayment of R" + (Number(r.amount) || 0) + " from " + (r.memberName || "member"), details: { repaymentId: ref.id, loanId: r.loanId } });
  return ref.id;
}

export async function deleteRepayment(id, rid) {
  await deleteDoc(doc(db, "stokvels", id, "repayments", rid));
  await logStokvelAudit(id, { action: "delete_repayment", summary: "Deleted repayment " + rid, details: { rid } });
}

// ── Expenses (money out that isn't a payout or loan: bank charges, events, other) ──

export async function loadExpenses(id) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "expenses"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addExpense(id, e) {
  const ref = await addDoc(collection(db, "stokvels", id, "expenses"), {
    category: e.category || "other", date: e.date, amount: Number(e.amount) || 0,
    note: e.note || "", source: e.source || "manual", __bankTxnId: e.__bankTxnId || null,
    receipts: e.receipts || (e.receipt ? [e.receipt] : []), // supports MULTIPLE proofs
    recordedBy: actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_expense", summary: "Recorded expense of R" + (Number(e.amount) || 0) + " (" + (e.category || "other") + ")" + (e.receipt ? " with receipt" : ""), details: { expenseId: ref.id } });
  return ref.id;
}

export async function deleteExpense(id, eid) {
  await deleteDoc(doc(db, "stokvels", id, "expenses", eid));
  await logStokvelAudit(id, { action: "delete_expense", summary: "Deleted expense " + eid, details: { eid } });
}

// ── Income (money in that isn't a contribution: bank interest earned, other) ──

export async function loadIncome(id) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "income"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addIncome(id, i) {
  const ref = await addDoc(collection(db, "stokvels", id, "income"), {
    category: i.category || "other", date: i.date, amount: Number(i.amount) || 0,
    note: i.note || "", source: i.source || "manual", __bankTxnId: i.__bankTxnId || null,
    receipt: i.receipt || null,
    recordedBy: actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_income", summary: "Recorded income of R" + (Number(i.amount) || 0) + " (" + (i.category || "other") + ")", details: { incomeId: ref.id } });
  return ref.id;
}

export async function deleteIncome(id, iid) {
  await deleteDoc(doc(db, "stokvels", id, "income", iid));
  await logStokvelAudit(id, { action: "delete_income", summary: "Deleted income " + iid, details: { iid } });
}

// Append receipt(s) to a money-out entry. Each receipt is stamped with WHO uploaded it
// and WHEN. Attaching does NOT verify — a second person must still confirm. kind =
// 'expenses' | 'payouts' | 'income'. Supports multiple receipts (each with an amount).
export async function attachReceipts(id, kind, docId, receipts) {
  const who = actor();
  const stamped = (receipts || []).map((r) => ({ ...r, uploadedBy: r.uploadedBy || who, uploadedAt: r.uploadedAt || Date.now() }));
  const ref = doc(db, "stokvels", id, kind, docId);
  const snap = await getDoc(ref);
  const cur = snap.exists() ? (Array.isArray(snap.data().receipts) ? snap.data().receipts : (snap.data().receipt ? [snap.data().receipt] : [])) : [];
  const merged = [...cur.filter(Boolean), ...stamped];
  const total = stamped.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  // Attaching a NEW receipt clears any prior verification — it must be re-checked.
  await setDoc(ref, { receipts: merged, receipt: null, verifiedBy: null, verifiedAt: null, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "attach_receipt", summary: who + " uploaded " + stamped.length + " receipt(s)" + (total ? " (R" + total + ")" : "") + " on " + kind.replace(/s$/, ""), details: { kind, docId, count: stamped.length, uploadedBy: who, total } });
}

// Second-person check (maker-checker). A DIFFERENT person from the uploader confirms
// the receipts are legible and reconcile to the money out → sets the verified badge.
// `verified:false` (with a reason) records a rejection and keeps the entry flagged.
export async function setVerification(id, kind, docId, { verified, note }) {
  const who = actor();
  const ref = doc(db, "stokvels", id, kind, docId);
  await setDoc(ref, verified
    ? { verifiedBy: who, verifiedAt: serverTimestamp(), verifyNote: note || "" }
    : { verifiedBy: null, verifiedAt: null, verifyNote: note || "" }, { merge: true });
  await logStokvelAudit(id, {
    action: verified ? "verify_txn" : "flag_txn",
    summary: who + (verified ? " checked & VERIFIED " : " flagged ") + kind.replace(/s$/, "") + (note ? " — " + note : ""),
    details: { kind, docId, checkedBy: who, verified: !!verified },
  });
}

// ── Bulk apply a reviewed statement import (one batch, one audit entry) ──
// buckets = { contributions:[], repayments:[], loans:[], expenses:[], income:[], payouts:[] }
export async function applyImport(id, buckets) {
  const b = buckets || {};
  const batch = writeBatch(db);
  const now = serverTimestamp();
  let n = 0;
  const col = (name) => collection(db, "stokvels", id, name);
  (b.contributions || []).forEach((c) => { n++; batch.set(doc(col("contributions")), { memberUid: c.memberUid || null, memberName: c.memberName || "", reference: c.reference || "", date: c.date, amount: Number(c.amount) || 0, method: "bank", status: "confirmed", source: "statementImport", note: c.note || "", recordedBy: actor(), at: now }); });
  (b.repayments || []).forEach((r) => { n++; batch.set(doc(col("repayments")), { loanId: r.loanId || null, memberUid: r.memberUid || null, memberName: r.memberName || "", date: r.date, amount: Number(r.amount) || 0, source: "statementImport", note: r.note || "", recordedBy: actor(), at: now }); });
  (b.loans || []).forEach((l) => { n++; batch.set(doc(col("loans")), { memberUid: l.memberUid || null, memberName: l.memberName || "", reference: l.reference || "", principal: Number(l.amount) || 0, interestType: l.interestType || "none", interestRate: Number(l.interestRate) || 0, numberOfRepayments: Number(l.numberOfRepayments) || 0, issueDate: l.date, dueDate: null, status: "active", note: l.note || "", source: "statementImport", recordedBy: actor(), at: now }); });
  (b.expenses || []).forEach((e) => { n++; batch.set(doc(col("expenses")), { category: e.category || "other", date: e.date, amount: Number(e.amount) || 0, note: e.note || "", source: "statementImport", recordedBy: actor(), at: now }); });
  (b.income || []).forEach((i) => { n++; batch.set(doc(col("income")), { category: i.category || "other", date: i.date, amount: Number(i.amount) || 0, note: i.note || "", source: "statementImport", recordedBy: actor(), at: now }); });
  (b.payouts || []).forEach((p) => { n++; batch.set(doc(col("payouts")), { memberUid: p.memberUid || null, memberName: p.memberName || "Group", date: p.date, amount: Number(p.amount) || 0, reason: p.note || "Statement withdrawal", source: "statementImport", recordedBy: actor(), at: now }); });
  batch.set(doc(col("auditLog")), { action: "reconcile_statement", summary: "Imported " + n + " item(s) from a bank statement", details: { count: n }, actor: actor(), at: now });
  await batch.commit();
  return n;
}

// ── Audit + convenience bulk load ──

export async function loadStokvelAudit(id, max) {
  const snap = await getDocs(query(collection(db, "stokvels", id, "auditLog"), orderBy("at", "desc")));
  return snap.docs.slice(0, max || 200).map((d) => ({ id: d.id, ...d.data() }));
}

export async function loadStokvelData(id) {
  const [stokvel, contributions, payouts, loans, repayments, expenses, income, claims, dependants, pendingChanges, amendments] = await Promise.all([
    loadStokvel(id), loadContributions(id), loadPayouts(id), loadLoans(id), loadRepayments(id), loadExpenses(id), loadIncome(id), loadClaims(id), loadDependants(id), loadPendingChanges(id), loadAmendments(id),
  ]);
  return { stokvel, contributions, payouts, loans, repayments, expenses, income, claims, dependants, pendingChanges, amendments };
}

// ── Governance: rule/settings change with optional two-person seconding ──
// When a stokvel has `requireSecondApproval`, a settings change is stored as a
// PENDING proposal instead of applied, and a DIFFERENT manager must approve it.
// Everything is recorded in the append-only audit log. (App-level control.)
export async function loadPendingChanges(id) {
  try {
    const snap = await getDocs(query(collection(db, "stokvels", id, "pendingChanges"), where("status", "==", "pending")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}

export async function proposeStokvelChange(id, patch, proposedByName) {
  const ref = await addDoc(collection(db, "stokvels", id, "pendingChanges"), {
    patch, status: "pending", proposedBy: (auth.currentUser && auth.currentUser.uid) || null,
    proposedByName: proposedByName || actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "propose_change", summary: "Proposed a settings change — awaiting a second approval", details: { patch } });
  return ref.id;
}

export async function approvePendingChange(id, pcId) {
  const ref = doc(db, "stokvels", id, "pendingChanges", pcId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("This proposal no longer exists.");
  const pc = snap.data();
  if (pc.status !== "pending") throw new Error("This proposal has already been dealt with.");
  await setDoc(doc(db, "stokvels", id), { ...(pc.patch || {}), updatedAt: serverTimestamp() }, { merge: true });
  await setDoc(ref, { status: "applied", approvedBy: (auth.currentUser && auth.currentUser.uid) || null, approvedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "apply_change", summary: "Settings change approved and applied", details: { patch: pc.patch } });
}

export async function rejectPendingChange(id, pcId) {
  await setDoc(doc(db, "stokvels", id, "pendingChanges", pcId), { status: "rejected", rejectedBy: (auth.currentUser && auth.currentUser.uid) || null, rejectedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "reject_change", summary: "A proposed settings change was rejected" });
}

// ── Corrections / amendments to recorded money entries (DUAL SIGN-OFF) ──
// A wrong contribution, loan or repayment is never edited outright. A chair or
// treasurer PROPOSES a correction (stored pending, original untouched); the
// COUNTERPART office-bearer must then CONFIRM before it is applied:
//   treasurer proposes → chairperson confirms, and vice versa (never the same
//   person). Propose / confirm / reject are all written to the append-only audit
//   log with the before → after values, so who-amended-and-who-confirmed is on
//   permanent record. (App-level control, mirroring proposeStokvelChange.)
const AMENDABLE = { contributions: true, loans: true, repayments: true };

// The signed-in user's role in THIS stokvel, read from the group doc (not trusted
// from the client) so the counterpart gate can't be spoofed by a passed argument.
function roleInStokvel(sdata) {
  const uid = auth.currentUser && auth.currentUser.uid;
  const m = uid && sdata && sdata.members && sdata.members[uid];
  return (m && m.role) || null;
}

export async function loadAmendments(id) {
  try {
    const snap = await getDocs(query(collection(db, "stokvels", id, "amendments"), where("status", "==", "pending")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}

// changes = { field: newValue } (only the fields being corrected); before = the same
// fields' current values (for the audit trail). kind = contributions|loans|repayments.
export async function proposeAmendment(id, { kind, targetId, before, changes, reason, memberName }) {
  if (!AMENDABLE[kind]) throw new Error("This type of entry can't be corrected here.");
  if (!targetId) throw new Error("Missing the entry to correct.");
  if (!changes || !Object.keys(changes).length) throw new Error("Nothing to correct — no values were changed.");
  const ssnap = await getDoc(doc(db, "stokvels", id));
  const role = roleInStokvel(ssnap.exists() ? ssnap.data() : {});
  if (role !== "chair" && role !== "treasurer") throw new Error("Only the chairperson or treasurer can propose a correction.");
  const uid = (auth.currentUser && auth.currentUser.uid) || null;
  const counterpart = role === "treasurer" ? "chairperson" : "treasurer";
  const ref = await addDoc(collection(db, "stokvels", id, "amendments"), {
    kind, targetId,
    before: before || null,
    changes: changes || {},
    reason: reason || "",
    memberName: memberName || "",
    status: "pending",
    proposedBy: uid, proposedByName: actor(), proposedByRole: role,
    at: serverTimestamp(),
    confirmedBy: null, confirmedByName: null, confirmedByRole: null, confirmedAt: null,
  });
  await logStokvelAudit(id, {
    action: "propose_amendment",
    summary: actor() + " (" + role + ") proposed a correction to a " + kind.replace(/s$/, "") + (memberName ? " for " + memberName : "") + " — awaiting " + counterpart + " confirmation",
    details: { amendmentId: ref.id, kind, targetId, before: before || null, changes, reason: reason || "" },
  });
  return ref.id;
}

// The counterpart office-bearer confirms → the correction is applied to the original
// entry and the amendment is stamped with who confirmed it.
export async function confirmAmendment(id, amendmentId) {
  const aref = doc(db, "stokvels", id, "amendments", amendmentId);
  const [asnap, ssnap] = await Promise.all([getDoc(aref), getDoc(doc(db, "stokvels", id))]);
  if (!asnap.exists()) throw new Error("This correction no longer exists.");
  const a = asnap.data();
  if (a.status !== "pending") throw new Error("This correction has already been dealt with.");
  const role = roleInStokvel(ssnap.exists() ? ssnap.data() : {});
  const uid = (auth.currentUser && auth.currentUser.uid) || null;
  if (role !== "chair" && role !== "treasurer") throw new Error("Only the chairperson or treasurer can confirm a correction.");
  if (uid && a.proposedBy && uid === a.proposedBy) throw new Error("A correction must be confirmed by the OTHER office-bearer — not the person who proposed it.");
  const needed = a.proposedByRole === "treasurer" ? "chair" : "treasurer";
  if (role !== needed) {
    const neededLabel = needed === "chair" ? "chairperson" : "treasurer";
    const byLabel = a.proposedByRole === "treasurer" ? "treasurer" : "chairperson";
    throw new Error("This correction was proposed by the " + byLabel + ", so it must be confirmed by the " + neededLabel + ".");
  }
  // Apply the correction to the original entry and record that it was amended.
  await setDoc(doc(db, "stokvels", id, a.kind, a.targetId), { ...(a.changes || {}), amendedAt: serverTimestamp(), amendedBy: actor(), amendmentId }, { merge: true });
  await setDoc(aref, { status: "applied", confirmedBy: uid, confirmedByName: actor(), confirmedByRole: role, confirmedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, {
    action: "confirm_amendment",
    summary: actor() + " (" + role + ") confirmed & applied a correction to a " + a.kind.replace(/s$/, "") + (a.memberName ? " for " + a.memberName : "") + " — proposed by " + (a.proposedByName || "?"),
    details: { amendmentId, kind: a.kind, targetId: a.targetId, before: a.before || null, changes: a.changes || {}, proposedBy: a.proposedByName, reason: a.reason || "" },
  });
}

// Decline a pending correction. The proposer can cancel their own; the counterpart can
// reject it. Either way it is recorded and the original entry stays as it was.
export async function rejectAmendment(id, amendmentId, reason) {
  const aref = doc(db, "stokvels", id, "amendments", amendmentId);
  const asnap = await getDoc(aref);
  if (!asnap.exists()) throw new Error("This correction no longer exists.");
  const a = asnap.data();
  if (a.status !== "pending") throw new Error("This correction has already been dealt with.");
  const uid = (auth.currentUser && auth.currentUser.uid) || null;
  await setDoc(aref, { status: "rejected", rejectedBy: uid, rejectedByName: actor(), rejectedAt: serverTimestamp(), rejectReason: reason || "" }, { merge: true });
  await logStokvelAudit(id, {
    action: "reject_amendment",
    summary: actor() + " rejected a proposed correction to a " + a.kind.replace(/s$/, "") + (a.memberName ? " for " + a.memberName : "") + (reason ? " — " + reason : ""),
    details: { amendmentId, kind: a.kind, targetId: a.targetId, reason: reason || "" },
  });
}

// ── Living constitution (editable + versioned) and meeting minutes ──
// The constitution text + version live on the stokvel doc; every amendment appends a
// history record (who/when/what) so the change trail is permanent. Minutes are their
// own subcollection. Both are writable by chair/treasurer (grant a "secretary" the
// treasurer role + the governance permission); all members can read.
export async function saveConstitutionVersion(id, text, summary, byName) {
  const ref = doc(db, "stokvels", id);
  const snap = await getDoc(ref);
  const version = (snap.exists() ? (snap.data().constitutionVersion || 0) : 0) + 1;
  await setDoc(ref, { constitutionText: text || "", constitutionVersion: version, constitutionUpdatedAt: serverTimestamp(), constitutionUpdatedBy: byName || actor() }, { merge: true });
  await addDoc(collection(db, "stokvels", id, "constitutionHistory"), {
    version, text: text || "", summary: summary || "",
    changedBy: (auth.currentUser && auth.currentUser.uid) || null, changedByName: byName || actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "amend_constitution", summary: "Constitution amended → version " + version + (summary ? " (" + summary + ")" : ""), details: { version } });
  return version;
}
export async function loadConstitutionHistory(id) {
  try { const snap = await getDocs(query(collection(db, "stokvels", id, "constitutionHistory"), orderBy("version", "desc"))); return snap.docs.map((d) => ({ id: d.id, ...d.data() })); } catch (e) { return []; }
}
export async function loadMinutes(id) {
  try { const snap = await getDocs(query(collection(db, "stokvels", id, "minutes"), orderBy("date", "desc"))); return snap.docs.map((d) => ({ id: d.id, ...d.data() })); } catch (e) { return []; }
}
export async function addMinute(id, m) {
  const ref = await addDoc(collection(db, "stokvels", id, "minutes"), {
    title: m.title || "", date: m.date, time: m.time || "", attendees: m.attendees || "", absentees: m.absentees || "", guests: m.guests || "",
    nextMeeting: m.nextMeeting || "", discussion: Array.isArray(m.discussion) ? m.discussion : [], notes: m.notes || "",
    body: m.body || "", attachment: m.attachment || null,
    recordedBy: (auth.currentUser && auth.currentUser.uid) || null, recordedByName: m.recordedByName || actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_minutes", summary: "Saved meeting minutes — " + (m.title || m.date || ""), details: { minuteId: ref.id } });
  return ref.id;
}
export async function deleteMinute(id, mid) {
  await deleteDoc(doc(db, "stokvels", id, "minutes", mid));
  await logStokvelAudit(id, { action: "delete_minutes", summary: "Deleted meeting minutes " + mid });
}

// ── Other governance documents ── any file the group wants on record, each with a
// member-given heading (e.g. AGM resolution, bank mandate, membership register).
// Visible to all members; managed by governance/chair. Mirrors the minutes pattern.
export async function loadStokvelDocs(id) {
  try { const snap = await getDocs(query(collection(db, "stokvels", id, "documents"), orderBy("at", "desc"))); return snap.docs.map((d) => ({ id: d.id, ...d.data() })); } catch (e) { return []; }
}
export async function addStokvelDoc(id, { heading, file, byName }) {
  const ref = await addDoc(collection(db, "stokvels", id, "documents"), {
    heading: heading || "Document", file: file || null,
    uploadedBy: (auth.currentUser && auth.currentUser.uid) || null, uploadedByName: byName || actor(), at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_document", summary: "Uploaded document — " + (heading || ""), details: { docId: ref.id } });
  return ref.id;
}
export async function deleteStokvelDoc(id, did) {
  await deleteDoc(doc(db, "stokvels", id, "documents", did));
  await logStokvelAudit(id, { action: "delete_document", summary: "Deleted document " + did });
}

// Read recent activity from the append-only audit log (who did what, when).
export async function loadStokvelAuditLog(id, max) {
  try {
    const snap = await getDocs(query(collection(db, "stokvels", id, "auditLog"), orderBy("at", "desc"), limit(max || 60)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}

// ── Covered family (dependants) — burial societies only ──
// Each member registers the family members their cover extends to. A claim can then
// be lodged against a registered dependant, so the society enforces "who is covered".
export async function loadDependants(id) {
  try {
    const snap = await getDocs(collection(db, "stokvels", id, "dependants"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}

export async function addDependant(id, d) {
  const ref = await addDoc(collection(db, "stokvels", id, "dependants"), {
    memberKey: d.memberKey || "",
    memberName: d.memberName || "",
    name: d.name || "",
    relationship: d.relationship || "other",
    dob: d.dob || "",
    note: d.note || "",
    // Waiting period runs from this date (when the person was added/changed).
    coveredFrom: d.coveredFrom || new Date().toISOString().slice(0, 10),
    recordedBy: actor(),
    at: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_dependant", summary: "Added covered family member " + (d.name || "") + " (" + (d.memberName || "") + ")", details: { dependantId: ref.id } });
  return ref.id;
}

export async function deleteDependant(id, depId) {
  await deleteDoc(doc(db, "stokvels", id, "dependants", depId));
  await logStokvelAudit(id, { action: "remove_dependant", summary: "Removed covered family member " + depId, details: { depId } });
}

// ── Burial-society claims (only used by stokvels of type 'burial') ──
// A claim is the funeral-payout lifecycle: lodged → approved → paid (or rejected).
// It carries the deceased's name + relationship and an optional death-certificate
// attachment. Marking it PAID records the money through the normal payout pipeline
// (so cash, reconciliation, transparency and the audit trail all stay correct) and
// links the two records together.
export async function loadClaims(id) {
  try {
    const snap = await getDocs(query(collection(db, "stokvels", id, "claims"), orderBy("claimDate", "desc")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}

export async function addClaim(id, c) {
  const ref = await addDoc(collection(db, "stokvels", id, "claims"), {
    memberUid: c.memberUid || null,
    memberName: c.memberName || "",
    reference: c.reference || "",
    deceasedName: c.deceasedName || "",
    relationship: c.relationship || "other",
    claimDate: c.claimDate,
    coverAmount: Number(c.coverAmount) || 0,
    status: "lodged",
    amountPaid: 0,
    datePaid: null,
    payoutId: null,
    certificate: c.certificate || null,
    note: c.note || "",
    recordedBy: actor(),
    at: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logStokvelAudit(id, { action: "add_claim", summary: "Burial claim lodged for " + (c.deceasedName || c.memberName || "member") + " (" + (c.memberName || "") + ")", details: { claimId: ref.id } });
  return ref.id;
}

export async function updateClaim(id, cid, patch) {
  await setDoc(doc(db, "stokvels", id, "claims", cid), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "update_claim", summary: "Updated burial claim " + cid, details: { cid, patch: { ...patch, certificate: patch.certificate ? "(attachment)" : undefined } } });
}

// Mark a claim PAID: records the money as a payout (existing pipeline) and links it.
export async function payClaim(id, cid, { amount, date }) {
  const ref = doc(db, "stokvels", id, "claims", cid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Claim not found.");
  const c = snap.data();
  const payoutId = await addPayout(id, {
    memberUid: c.memberUid || null,
    memberName: c.memberName || "Member",
    date,
    amount: Number(amount) || 0,
    reason: "Burial claim — " + (c.deceasedName || c.memberName || "member"),
  });
  await setDoc(ref, { status: "paid", amountPaid: Number(amount) || 0, datePaid: date, payoutId, updatedAt: serverTimestamp() }, { merge: true });
  await logStokvelAudit(id, { action: "pay_claim", summary: "Paid burial claim of R" + (Number(amount) || 0) + " to " + (c.memberName || "member") + " for " + (c.deceasedName || "—"), details: { cid, payoutId } });
}

export async function deleteClaim(id, cid) {
  await deleteDoc(doc(db, "stokvels", id, "claims", cid));
  await logStokvelAudit(id, { action: "delete_claim", summary: "Deleted burial claim " + cid, details: { cid } });
}

// Read any bank-feed transactions already synced for this stokvel (populated by the
// reused Stitch plumbing). Returns [] while the feed is dormant.
export async function loadStokvelBankTransactions(id) {
  try {
    const snap = await getDocs(collection(db, "stokvels", id, "bankFeedTransactions"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) { return []; }
}
